/**
 * Modal « Transférer les copies d'une matière vers une autre »
 *
 * Cas d'usage : un agent a associé par erreur des copies à Électrotechnique
 * au lieu d'Électronique II. Au lieu de dissocier une par une puis réassocier,
 * on transfère toutes les copies concernées en un seul clic.
 *
 * Flux en 3 étapes :
 *   1. SÉLECTION — table cochable des couples (matière × formateur × classe)
 *      avec cases à cocher, recherche, tri
 *   2. PREVIEW  — récapitulatif détaillé : étudiants + classes + formateurs
 *   3. VALIDATION — confirmation puis exécution
 */
import React, { useState, useMemo, useEffect } from 'react';
import { X, ArrowRightLeft, AlertTriangle, CheckCircle2, Search, ArrowLeft } from 'lucide-react';
import { useGetMatieresQuery } from '../../services/matieresApi';
import { useGetStatsMatiereEnseignantQuery, useTransfererCopiesMutation } from '../../services/copiesApi';
import { useGetAttributionsAnneeQuery } from '../../services/formateursApi';
import { useGetAnneeActiveQuery } from '../../services/anneeAcademiqueApi';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (msg: string) => void;
}

type Etape = 'selection' | 'preview' | 'termine';

interface LigneCouple {
  key: string;                          // matiere_id + '::' + enseignant_id
  matiere_id: number;
  matiere_nom: string;
  niveau: string;                       // ex "L1AI" — affichage principal
  enseignant_id: number | null;
  enseignant_nom: string;
  classes: Array<{ classe: string; count: number }>;
  total: number;
}

const TransfererMatiereModal: React.FC<Props> = ({ isOpen, onClose, onSuccess }) => {
  const { data: matieres = [] } = useGetMatieresQuery();
  const { data: stats, isLoading: isLoadingStats, refetch } = useGetStatsMatiereEnseignantQuery(
    undefined, { skip: !isOpen }
  );
  const { data: anneeActive } = useGetAnneeActiveQuery();
  const { data: attributionsAnnee } = useGetAttributionsAnneeQuery(
    { annee_academique: anneeActive?.libelle ?? '' },
    { skip: !anneeActive || !isOpen }
  );
  const [transferer, { isLoading: isLoadingMut }] = useTransfererCopiesMutation();

  const [etape, setEtape] = useState<Etape>('selection');
  const [selection, setSelection] = useState<Set<string>>(new Set());
  const [classesActives, setClassesActives] = useState<Set<string>>(new Set());
  const [destIds, setDestIds] = useState<Set<number>>(new Set());
  // Overrides manuels pour classes ambiguës ou sans mapping automatique
  const [mappingOverride, setMappingOverride] = useState<Record<string, number>>({});
  const [searchLignes, setSearchLignes] = useState('');
  const [searchDest, setSearchDest] = useState('');
  const [typeExamen, setTypeExamen] = useState<'' | 'NORMAL' | 'RATTRAPAGE'>('');
  const [forceNiveau, setForceNiveau] = useState(false);
  const [preview, setPreview] = useState<any>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Reset à l'ouverture
  useEffect(() => {
    if (isOpen) {
      setEtape('selection');
      setSelection(new Set());
      setClassesActives(new Set());
      setDestIds(new Set());
      setMappingOverride({});
      setSearchLignes('');
      setSearchDest('');
      setTypeExamen('');
      setForceNiveau(false);
      setPreview(null);
      setMessage(null);
    }
  }, [isOpen]);

  // Aplatir stats en lignes (matière × formateur)
  const lignes = useMemo<LigneCouple[]>(() => {
    if (!stats?.matieres) return [];
    const out: LigneCouple[] = [];
    for (const m of stats.matieres) {
      if (m.enseignants.length === 0) {
        // Matière sans aucun formateur associé
        out.push({
          key: `${m.matiere_id}::0`,
          matiere_id: m.matiere_id,
          matiere_nom: m.matiere_nom,
          niveau: m.niveau || '',
          enseignant_id: null,
          enseignant_nom: '— Sans formateur —',
          classes: m.classes.map(c => ({ classe: c.classe, count: c.count })),
          total: m.total,
        });
      } else {
        for (const ens of m.enseignants) {
          out.push({
            key: `${m.matiere_id}::${ens.id}`,
            matiere_id: m.matiere_id,
            matiere_nom: m.matiere_nom,
            niveau: m.niveau || '',
            enseignant_id: ens.id,
            enseignant_nom: ens.nom,
            classes: ens.classes,
            total: ens.count,
          });
        }
      }
    }
    return out;
  }, [stats]);

  // Filtrage par recherche
  const lignesFiltrees = useMemo(() => {
    if (!searchLignes.trim()) return lignes;
    const q = searchLignes.toLowerCase();
    return lignes.filter(l =>
      l.matiere_nom.toLowerCase().includes(q) ||
      l.niveau.toLowerCase().includes(q) ||
      l.enseignant_nom.toLowerCase().includes(q) ||
      l.classes.some(c => c.classe.toLowerCase().includes(q))
    );
  }, [lignes, searchLignes]);

  // Matières cibles cochées (résolues)
  const destsList = useMemo(
    () => (matieres as any[]).filter(m => destIds.has(m.id)),
    [matieres, destIds]
  );
  const nbCoupleSel = selection.size;
  const lignesSel = useMemo(() => lignes.filter(l => selection.has(l.key)), [lignes, selection]);

  // Classes disponibles = union des classes de toutes les sources cochées
  const classesDisponibles = useMemo(() => {
    const map = new Map<string, number>();  // classe → total copies
    for (const l of lignesSel) {
      for (const c of l.classes) {
        map.set(c.classe, (map.get(c.classe) || 0) + c.count);
      }
    }
    return Array.from(map.entries())
      .map(([classe, count]) => ({ classe, count }))
      .sort((a, b) => a.classe.localeCompare(b.classe));
  }, [lignesSel]);

  // Quand la sélection change → reset classes actives à "toutes"
  useEffect(() => {
    setClassesActives(new Set(classesDisponibles.map(c => c.classe)));
  }, [classesDisponibles]);

  // Copies effectivement à transférer (après filtre classes)
  const nbCopiesSel = useMemo(() => {
    let total = 0;
    for (const l of lignesSel) {
      for (const c of l.classes) {
        if (classesActives.has(c.classe)) total += c.count;
      }
    }
    return total;
  }, [lignesSel, classesActives]);

  // Semestres des sources cochées (pour filtrer les cibles au même semestre)
  const semestresSources = useMemo(() => {
    const s = new Set<number>();
    for (const l of lignesSel) {
      const mat = (matieres as any[]).find(m => m.id === l.matiere_id);
      if (mat?.semestre != null) s.add(mat.semestre);
    }
    return s;
  }, [lignesSel, matieres]);

  // Lookup rapide : matiere_id → stats (pour afficher classes/formateurs sur la cible)
  const statsByMat = useMemo(() => {
    const map = new Map<number, any>();
    if (stats?.matieres) {
      for (const m of stats.matieres) map.set(m.matiere_id, m);
    }
    return map;
  }, [stats]);

  // Filtre matières cibles : semestre commun + recherche textuelle
  // → structure IDENTIQUE aux sources : une ligne par matière avec classe/formateur/copies
  const lignesCibles = useMemo<LigneCouple[]>(() => {
    let list = matieres as any[];
    // 1. Filtre semestre
    if (semestresSources.size > 0) {
      list = list.filter(m => semestresSources.has(m.semestre));
    }
    // 2. Filtre recherche
    if (searchDest.trim()) {
      const ql = searchDest.toLowerCase();
      list = list.filter(m =>
        (m.nom_matiere || '').toLowerCase().includes(ql) ||
        (m.niveau || '').toLowerCase().includes(ql) ||
        (m.code_matiere || '').toLowerCase().includes(ql)
      );
    }
    // 3. Aplatir en LigneCouple (1 ligne = 1 matière, formateur via attributionsAnnee, copies via stats)
    // ATTENTION : `niveau` d'une Matiere = "L1"/"L2" (niveau court)
    //             `classe` d'une Matiere = "L1AI"/"L1AII" (vrai code de classe utilisé pour matcher les étudiants)
    const attr = attributionsAnnee || {};
    return list.slice(0, 300).map(m => {
      const st = statsByMat.get(m.id);
      const a = attr[String(m.id)];
      let enseignant_id: number | null = null;
      let enseignant_nom = '— À définir —';
      if (a?.formateur_id) {
        enseignant_id = a.formateur_id;
        enseignant_nom = a.formateur_nom || '';
      } else if (st?.enseignants?.length) {
        const first = st.enseignants[0];
        enseignant_id = first.id;
        enseignant_nom = st.enseignants.length > 1
          ? `${first.nom} +${st.enseignants.length - 1}`
          : first.nom;
      }
      const classeReelle = m.classe || m.niveau || '';   // ex "L1AI"
      return {
        key: `${m.id}::${enseignant_id ?? 0}`,
        matiere_id: m.id,
        matiere_nom: m.nom_matiere,
        niveau: classeReelle,                              // exposé comme "niveau" pour la ligne (utilisé partout)
        enseignant_id,
        enseignant_nom,
        classes: [{ classe: classeReelle, count: st?.total || 0 }],
        total: st?.total || 0,
      };
    }).sort((a, b) => a.niveau.localeCompare(b.niveau) || a.matiere_nom.localeCompare(b.matiere_nom));
  }, [matieres, semestresSources, searchDest, attributionsAnnee, statsByMat]);

  const toggleClasse = (classe: string) => {
    setClassesActives(prev => {
      const next = new Set(prev);
      next.has(classe) ? next.delete(classe) : next.add(classe);
      return next;
    });
  };
  const toggleAllClasses = () => {
    if (classesActives.size === classesDisponibles.length) {
      setClassesActives(new Set());
    } else {
      setClassesActives(new Set(classesDisponibles.map(c => c.classe)));
    }
  };

  const toggleDest = (id: number) => {
    setDestIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };
  const toggleAllCibles = (ids: number[]) => {
    const allSel = ids.every(id => destIds.has(id));
    setDestIds(prev => {
      const next = new Set(prev);
      if (allSel) {
        ids.forEach(id => next.delete(id));
      } else {
        ids.forEach(id => next.add(id));
      }
      return next;
    });
  };

  // Mapping automatique : pour chaque classe active, cherche la cible cochée
  // dont le niveau correspond exactement à la classe. Sinon, laisse "non résolue".
  const mappingResolu = useMemo(() => {
    const map: Record<string, number | null> = {};
    for (const c of classesDisponibles) {
      if (!classesActives.has(c.classe)) continue;
      // Override manuel prioritaire
      if (mappingOverride[c.classe] && destIds.has(mappingOverride[c.classe])) {
        map[c.classe] = mappingOverride[c.classe];
        continue;
      }
      // Match par niveau exact
      const match = destsList.filter(d => (d.classe || d.niveau) === c.classe);
      if (match.length === 1) {
        map[c.classe] = match[0].id;
      } else if (match.length > 1) {
        // Ambigu : prendre la 1ère par défaut mais signaler
        map[c.classe] = match[0].id;
      } else {
        map[c.classe] = null;  // pas de cible correspondante
      }
    }
    return map;
  }, [classesDisponibles, classesActives, destsList, destIds, mappingOverride]);

  const classesSansCible = Object.entries(mappingResolu).filter(([, v]) => v === null).map(([k]) => k);
  const classesAmbigues = useMemo(() => {
    const out: string[] = [];
    for (const c of classesDisponibles) {
      if (!classesActives.has(c.classe)) continue;
      if (mappingOverride[c.classe]) continue;
      const match = destsList.filter(d => (d.classe || d.niveau) === c.classe);
      if (match.length > 1) out.push(c.classe);
    }
    return out;
  }, [classesDisponibles, classesActives, destsList, mappingOverride]);

  const toggleLigne = (key: string) => {
    setSelection(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const toggleAll = () => {
    if (selection.size === lignesFiltrees.length) {
      setSelection(new Set());
    } else {
      setSelection(new Set(lignesFiltrees.map(l => l.key)));
    }
  };

  const lancerPreview = async () => {
    setMessage(null);
    if (selection.size === 0) {
      setMessage({ type: 'error', text: 'Cochez au moins une ligne à transférer.' });
      return;
    }
    const sources = lignesSel.map(l => ({
      matiere_id: l.matiere_id,
      enseignant_id: l.enseignant_id,
    }));
    try {
      const params: any = { sources, dry_run: true };
      // Envoi soit dests_by_classe (multi-cibles), soit dest_matiere_id (unique)
      const validMapping: Record<string, number> = {};
      for (const [cls, id] of Object.entries(mappingResolu)) {
        if (id != null) validMapping[cls] = id;
      }
      if (Object.keys(validMapping).length > 1) {
        params.dests_by_classe = validMapping;
      } else if (Object.keys(validMapping).length === 1) {
        // Un seul mapping : envoyer aussi comme dest_matiere_id pour compatibilité
        params.dests_by_classe = validMapping;
      } else if (destsList.length === 1) {
        // Aucune classe résolue mais UNE cible cochée → toutes copies vers elle
        params.dest_matiere_id = destsList[0].id;
      }
      if (typeExamen) params.type_examen = typeExamen;
      if (classesActives.size > 0 && classesActives.size < classesDisponibles.length) {
        params.classes = Array.from(classesActives);
      }
      const res: any = await transferer(params).unwrap();
      setPreview(res);
      setEtape('preview');
    } catch (e: any) {
      setMessage({ type: 'error', text: e?.data?.error || 'Erreur lors du preview' });
    }
  };

  const lancerTransfert = async () => {
    setMessage(null);
    if (destIds.size === 0) { setMessage({ type: 'error', text: 'Au moins une matière cible requise.' }); return; }
    if (!preview?.nb_copies) { setMessage({ type: 'error', text: 'Aucune copie à transférer.' }); return; }
    const nbCibles = new Set(Object.values(mappingResolu).filter(v => v != null)).size || destIds.size;
    if (!window.confirm(
      `Confirmer le transfert de ${preview.nb_copies} copie(s) vers ${nbCibles} matière(s) cible(s) ?\n\n` +
      `Cette action affecte ${preview.nb_etudiants_distincts} étudiant(s) répartis ` +
      `dans ${preview.nb_classes_distinctes} classe(s), enseignés par ${preview.nb_formateurs_distincts} formateur(s).\n\n` +
      `Traçabilité complète dans l'historique.`
    )) return;
    const sources = lignesSel.map(l => ({
      matiere_id: l.matiere_id,
      enseignant_id: l.enseignant_id,
    }));
    try {
      const params: any = {
        sources,
        force_niveau_different: forceNiveau,
      };
      const validMapping: Record<string, number> = {};
      for (const [cls, id] of Object.entries(mappingResolu)) {
        if (id != null) validMapping[cls] = id;
      }
      if (Object.keys(validMapping).length > 0) {
        params.dests_by_classe = validMapping;
      } else if (destsList.length === 1) {
        params.dest_matiere_id = destsList[0].id;
      }
      if (typeExamen) params.type_examen = typeExamen;
      if (classesActives.size > 0 && classesActives.size < classesDisponibles.length) {
        params.classes = Array.from(classesActives);
      }
      const res: any = await transferer(params).unwrap();
      setMessage({ type: 'success', text: res.message });
      setEtape('termine');
      refetch();
      if (onSuccess) onSuccess(res.message);
    } catch (e: any) {
      setMessage({ type: 'error', text: e?.data?.error || 'Erreur lors du transfert' });
    }
  };

  if (!isOpen) return null;

  // Alerte niveaux différents : au moins une cible cochée a un niveau non couvert par les sources
  const niveauxSources = new Set(lignes.filter(l => selection.has(l.key)).map(l => l.niveau).filter(Boolean));
  const niveauxDifferents = Boolean(
    destsList.length > 0 &&
    niveauxSources.size > 0 &&
    destsList.some(d => (d.classe || d.niveau) && !niveauxSources.has(d.classe || d.niveau))
  );

  return (
    <div className="fixed inset-0 z-[85] bg-black/60 flex items-center justify-center p-3" onClick={onClose}>
      <div
        className="w-full max-w-6xl max-h-[94vh] rounded-xl shadow-2xl flex flex-col bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ArrowRightLeft size={20} className="text-rose-600" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Transférer les copies vers une autre matière
            </h2>
            <span className={`px-2 py-0.5 rounded text-[10px] font-semibold ${
              etape === 'selection' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300' :
              etape === 'preview' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300' :
              'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
            }`}>
              {etape === 'selection' ? '① Sélection' : etape === 'preview' ? '② Prévisualisation' : '③ Terminé'}
            </span>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800">
            <X size={18} />
          </button>
        </div>

        {/* ═══════════════ ÉTAPE 1 : SÉLECTION ═══════════════ */}
        {etape === 'selection' && (
          <>
            <div className="px-5 py-2 bg-rose-50 dark:bg-rose-950/30 border-b border-rose-200 dark:border-rose-800 text-xs text-rose-800 dark:text-rose-300">
              Cochez les matières mal renseignées (avec leur formateur), puis choisissez la matière cible.
              Toutes les copies concernées seront ré-attribuées.
            </div>

            <div className="flex-1 overflow-auto p-5 space-y-4 text-sm">
              {/* Recherche + filtres */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="md:col-span-2">
                  <label className="block text-[11px] text-gray-500 mb-0.5">Rechercher (matière / classe / formateur)</label>
                  <div className="relative">
                    <Search size={12} className="absolute left-2 top-2 text-gray-400" />
                    <input
                      type="text"
                      value={searchLignes}
                      onChange={(e) => setSearchLignes(e.target.value)}
                      placeholder="Ex : Électro, L1AI, Kifouani…"
                      className="w-full pl-7 pr-2 py-1.5 text-xs rounded border bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-700"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-[11px] text-gray-500 mb-0.5">Type d'examen</label>
                  <select
                    value={typeExamen}
                    onChange={(e) => setTypeExamen(e.target.value as any)}
                    className="w-full px-2 py-1.5 text-xs rounded border bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-700"
                  >
                    <option value="">Tous</option>
                    <option value="NORMAL">Ordinaire</option>
                    <option value="RATTRAPAGE">Rattrapage</option>
                  </select>
                </div>
              </div>

              {/* Table cochable */}
              <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                <div className="max-h-[42vh] overflow-y-auto">
                  <table className="w-full text-[11px]">
                    <thead className="bg-gray-100 dark:bg-gray-800 sticky top-0 z-10">
                      <tr className="text-gray-600 dark:text-gray-300">
                        <th className="px-2 py-1.5 w-8 text-center">
                          <input
                            type="checkbox"
                            checked={lignesFiltrees.length > 0 && selection.size === lignesFiltrees.length}
                            onChange={toggleAll}
                            title="Tout cocher/décocher"
                          />
                        </th>
                        <th className="px-2 py-1.5 text-left">Matière</th>
                        <th className="px-2 py-1.5 text-left">Classe(s)</th>
                        <th className="px-2 py-1.5 text-left">Formateur</th>
                        <th className="px-2 py-1.5 text-right">Copies</th>
                      </tr>
                    </thead>
                    <tbody>
                      {isLoadingStats && (
                        <tr><td colSpan={5} className="px-2 py-4 text-center text-gray-500">Chargement…</td></tr>
                      )}
                      {!isLoadingStats && lignesFiltrees.length === 0 && (
                        <tr><td colSpan={5} className="px-2 py-4 text-center text-gray-500">Aucune matière associée</td></tr>
                      )}
                      {lignesFiltrees.map(l => (
                        <tr
                          key={l.key}
                          onClick={() => toggleLigne(l.key)}
                          className={`border-b border-gray-100 dark:border-gray-800 cursor-pointer ${
                            selection.has(l.key)
                              ? 'bg-blue-50 dark:bg-blue-950/30 hover:bg-blue-100 dark:hover:bg-blue-950/50'
                              : 'hover:bg-gray-50 dark:hover:bg-gray-800/50'
                          }`}
                        >
                          <td className="px-2 py-1.5 text-center">
                            <input
                              type="checkbox"
                              checked={selection.has(l.key)}
                              onChange={() => toggleLigne(l.key)}
                              onClick={(e) => e.stopPropagation()}
                            />
                          </td>
                          <td className="px-2 py-1.5">
                            <div className="font-medium text-gray-900 dark:text-gray-100">{l.matiere_nom}</div>
                            {l.niveau && (
                              <div className="text-[10px] text-gray-500 font-mono">{l.niveau}</div>
                            )}
                          </td>
                          <td className="px-2 py-1.5">
                            <div className="flex flex-wrap gap-1">
                              {l.classes.map(c => (
                                <span
                                  key={c.classe}
                                  className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-mono bg-indigo-100 dark:bg-indigo-900/40 text-indigo-800 dark:text-indigo-200"
                                >
                                  {c.classe} <span className="ml-1 opacity-70">({c.count})</span>
                                </span>
                              ))}
                            </div>
                          </td>
                          <td className="px-2 py-1.5">
                            <span className={l.enseignant_id ? 'text-gray-900 dark:text-gray-100' : 'text-amber-600 italic'}>
                              {l.enseignant_nom}
                            </span>
                          </td>
                          <td className="px-2 py-1.5 text-right font-mono font-semibold">{l.total}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Résumé sélection */}
              {nbCoupleSel > 0 && (
                <div className="rounded-lg bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 px-3 py-2 text-xs text-blue-900 dark:text-blue-200">
                  ✓ <strong>{nbCoupleSel}</strong> ligne(s) cochée(s)
                  {semestresSources.size > 0 && (
                    <span className="ml-2 text-[10px] font-mono opacity-70">
                      · sem{semestresSources.size > 1 ? 'estres' : 'estre'} {Array.from(semestresSources).sort().map(s => `S${s}`).join(', ')}
                    </span>
                  )}
                  <span className="ml-2">→ <strong>{nbCopiesSel}</strong> copie(s) après filtre classes</span>
                </div>
              )}

              {/* Classes à inclure — cases à cocher */}
              {classesDisponibles.length > 0 && (
                <div className="rounded-lg border border-indigo-300 dark:border-indigo-700 p-3 bg-indigo-50 dark:bg-indigo-950/30">
                  <div className="flex items-center justify-between mb-2">
                    <div className="font-semibold text-indigo-900 dark:text-indigo-200 text-xs uppercase tracking-wider">
                      Classes à inclure dans le transfert
                    </div>
                    <button
                      onClick={toggleAllClasses}
                      className="text-[10px] px-2 py-0.5 rounded border border-indigo-400 text-indigo-700 dark:text-indigo-300 hover:bg-indigo-100 dark:hover:bg-indigo-900/40"
                    >
                      {classesActives.size === classesDisponibles.length ? 'Tout décocher' : 'Tout cocher'}
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {classesDisponibles.map(c => (
                      <label
                        key={c.classe}
                        className={`inline-flex items-center gap-1 px-2 py-1 rounded border cursor-pointer text-[11px] ${
                          classesActives.has(c.classe)
                            ? 'bg-indigo-100 dark:bg-indigo-900/50 border-indigo-400 text-indigo-900 dark:text-indigo-100'
                            : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-500 line-through'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={classesActives.has(c.classe)}
                          onChange={() => toggleClasse(c.classe)}
                          className="w-3 h-3"
                        />
                        <span className="font-mono font-semibold">{c.classe}</span>
                        <span className="opacity-70">({c.count})</span>
                      </label>
                    ))}
                  </div>
                  {classesActives.size === 0 && (
                    <div className="mt-2 text-[11px] text-amber-700 dark:text-amber-300">
                      ⚠ Aucune classe cochée — le transfert n'affectera aucune copie
                    </div>
                  )}
                </div>
              )}

              {/* Matières cibles — table identique aux sources */}
              <div className="rounded-lg border border-emerald-300 dark:border-emerald-700 p-3 bg-emerald-50 dark:bg-emerald-950/30">
                <div className="flex items-center justify-between mb-2">
                  <div className="font-semibold text-emerald-900 dark:text-emerald-200 text-xs uppercase tracking-wider">
                    ② Matières CIBLES (cocher une ou plusieurs)
                    {semestresSources.size === 1 && (
                      <span className="ml-2 text-[10px] font-normal opacity-75">
                        · filtrées au semestre {Array.from(semestresSources).map(s => `S${s}`).join('/')}
                      </span>
                    )}
                    {semestresSources.size > 1 && (
                      <span className="ml-2 text-[10px] font-normal opacity-75">
                        · sources multi-semestres, filtre semestre relâché
                      </span>
                    )}
                  </div>
                  {destIds.size > 0 && (
                    <button
                      onClick={() => { setDestIds(new Set()); setMappingOverride({}); }}
                      className="text-[10px] px-2 py-0.5 rounded border border-emerald-400 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-100 dark:hover:bg-emerald-900/40"
                    >
                      Tout décocher ({destIds.size})
                    </button>
                  )}
                </div>
                <div className="relative mb-2">
                  <Search size={12} className="absolute left-2 top-2 text-gray-400" />
                  <input
                    type="text"
                    value={searchDest}
                    onChange={(e) => setSearchDest(e.target.value)}
                    placeholder="Rechercher matière cible (nom, niveau…)"
                    className="w-full pl-7 pr-2 py-1.5 text-xs rounded border bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-700"
                  />
                </div>
                <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden bg-white dark:bg-gray-800">
                  <div className="max-h-[38vh] overflow-y-auto">
                    <table className="w-full text-[11px]">
                      <thead className="bg-emerald-100 dark:bg-emerald-900/40 sticky top-0 z-10">
                        <tr className="text-emerald-800 dark:text-emerald-200">
                          <th className="px-2 py-1.5 w-8 text-center">
                            <input
                              type="checkbox"
                              checked={lignesCibles.length > 0 && lignesCibles.every(l => destIds.has(l.matiere_id))}
                              onChange={() => toggleAllCibles(lignesCibles.map(l => l.matiere_id))}
                              title="Tout cocher / décocher"
                            />
                          </th>
                          <th className="px-2 py-1.5 text-left">Matière</th>
                          <th className="px-2 py-1.5 text-left">Classe</th>
                          <th className="px-2 py-1.5 text-left">Formateur</th>
                          <th className="px-2 py-1.5 text-right">Copies associées</th>
                        </tr>
                      </thead>
                      <tbody>
                        {lignesCibles.length === 0 && (
                          <tr>
                            <td colSpan={5} className="px-2 py-4 text-center text-gray-500">
                              {semestresSources.size > 0
                                ? 'Aucune matière au semestre concerné'
                                : 'Cochez d\'abord une source pour filtrer par semestre'}
                            </td>
                          </tr>
                        )}
                        {lignesCibles.map(l => {
                          const checked = destIds.has(l.matiere_id);
                          return (
                            <tr
                              key={l.key}
                              onClick={() => toggleDest(l.matiere_id)}
                              className={`border-b border-gray-100 dark:border-gray-800 cursor-pointer ${
                                checked
                                  ? 'bg-emerald-50 dark:bg-emerald-950/40 hover:bg-emerald-100 dark:hover:bg-emerald-900/50'
                                  : 'hover:bg-gray-50 dark:hover:bg-gray-800/50'
                              }`}
                            >
                              <td className="px-2 py-1.5 text-center">
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={() => toggleDest(l.matiere_id)}
                                  onClick={(e) => e.stopPropagation()}
                                />
                              </td>
                              <td className="px-2 py-1.5">
                                <div className="font-medium text-gray-900 dark:text-gray-100">{l.matiere_nom}</div>
                              </td>
                              <td className="px-2 py-1.5">
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-mono bg-indigo-100 dark:bg-indigo-900/40 text-indigo-800 dark:text-indigo-200">
                                  {l.niveau || '—'}
                                </span>
                              </td>
                              <td className="px-2 py-1.5">
                                <span className={l.enseignant_id ? 'text-gray-900 dark:text-gray-100' : 'text-amber-600 italic'}>
                                  {l.enseignant_nom}
                                </span>
                              </td>
                              <td className="px-2 py-1.5 text-right font-mono">
                                {l.total > 0 ? (
                                  <span className="font-semibold text-blue-700 dark:text-blue-300">{l.total}</span>
                                ) : (
                                  <span className="text-gray-400">0</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
                {destIds.size > 0 && (
                  <div className="mt-1 text-[11px] text-emerald-800 dark:text-emerald-300">
                    ✓ {destIds.size} matière(s) cible(s) cochée(s)
                  </div>
                )}
              </div>

              {/* Mapping résolu : classe source → matière cible */}
              {destIds.size > 0 && classesActives.size > 0 && (
                <div className="rounded-lg border-2 border-purple-300 dark:border-purple-700 p-3 bg-purple-50 dark:bg-purple-950/30">
                  <div className="font-semibold text-purple-900 dark:text-purple-200 mb-2 text-xs uppercase tracking-wider">
                    ③ Routage classe → matière cible
                  </div>
                  <div className="text-[10px] text-purple-800 dark:text-purple-300 mb-2 opacity-80">
                    Chaque classe source est envoyée automatiquement vers la matière cible dont le niveau correspond.
                    Ajuste si nécessaire.
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-[11px]">
                      <thead>
                        <tr className="text-purple-700 dark:text-purple-300 text-[10px] uppercase">
                          <th className="px-2 py-1 text-left">Classe source</th>
                          <th className="px-2 py-1 text-center">→</th>
                          <th className="px-2 py-1 text-left">Matière cible</th>
                          <th className="px-2 py-1 text-right">Copies</th>
                        </tr>
                      </thead>
                      <tbody>
                        {classesDisponibles.filter(c => classesActives.has(c.classe)).map(c => {
                          const cibleId = mappingResolu[c.classe];
                          const cible = destsList.find(d => d.id === cibleId);
                          const candidats = destsList.filter(d => (d.classe || d.niveau) === c.classe);
                          const ambigu = candidats.length > 1;
                          return (
                            <tr key={c.classe} className="border-t border-purple-200 dark:border-purple-800">
                              <td className="px-2 py-1">
                                <span className="font-mono font-semibold bg-indigo-100 dark:bg-indigo-900/40 text-indigo-800 dark:text-indigo-200 px-1.5 py-0.5 rounded">
                                  {c.classe}
                                </span>
                              </td>
                              <td className="px-2 py-1 text-center text-purple-500">→</td>
                              <td className="px-2 py-1">
                                {cible ? (
                                  ambigu || destsList.length > 1 ? (
                                    <select
                                      value={cibleId || ''}
                                      onChange={(e) => setMappingOverride(prev => ({
                                        ...prev, [c.classe]: parseInt(e.target.value, 10)
                                      }))}
                                      className={`px-1 py-0.5 rounded border text-[11px] ${ambigu ? 'border-amber-400 bg-amber-50 dark:bg-amber-900/20' : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800'}`}
                                    >
                                      {destsList.map(d => (
                                        <option key={d.id} value={d.id}>
                                          {d.nom_matiere} ({d.classe || d.niveau})
                                        </option>
                                      ))}
                                    </select>
                                  ) : (
                                    <span className="text-emerald-800 dark:text-emerald-200">
                                      <strong>{cible.nom_matiere}</strong>
                                      <span className="ml-1 text-[10px] font-mono opacity-70">({cible.classe || cible.niveau})</span>
                                    </span>
                                  )
                                ) : (
                                  <span className="text-red-600 dark:text-red-400 italic text-[10px]">
                                    ⚠ Aucune cible avec niveau {c.classe}
                                  </span>
                                )}
                              </td>
                              <td className="px-2 py-1 text-right font-mono">{c.count}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  {classesSansCible.length > 0 && (
                    <div className="mt-2 text-[11px] text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/30 border border-red-300 dark:border-red-700 rounded p-2">
                      ⚠ Classes sans cible : <strong>{classesSansCible.join(', ')}</strong>.
                      Coche une matière cible dont le niveau correspond, ou décoche ces classes.
                    </div>
                  )}
                  {classesAmbigues.length > 0 && (
                    <div className="mt-2 text-[11px] text-amber-700 dark:text-amber-300">
                      ℹ Plusieurs candidats pour {classesAmbigues.join(', ')} — le 1er est pris par défaut, utilise le menu pour ajuster.
                    </div>
                  )}
                </div>
              )}

              {message && (
                <div className="rounded-lg p-2 text-xs bg-red-50 text-red-800 border border-red-300 dark:bg-red-900/30 dark:text-red-200">
                  {message.text}
                </div>
              )}
            </div>

            <div className="px-5 py-3 border-t border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50 flex items-center justify-between gap-2">
              <div className="text-[11px] text-gray-500">
                {lignesFiltrees.length} ligne(s) affichée(s) · {lignes.length} au total
              </div>
              <div className="flex gap-2">
                <button
                  onClick={onClose}
                  className="px-3 py-1.5 rounded border border-gray-300 dark:border-gray-600 text-xs text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  Fermer
                </button>
                <button
                  onClick={lancerPreview}
                  disabled={
                    nbCoupleSel === 0 ||
                    nbCopiesSel === 0 ||
                    destIds.size === 0 ||
                    classesSansCible.length > 0 ||
                    isLoadingMut
                  }
                  title={
                    destIds.size === 0 ? 'Coche au moins une matière cible' :
                    classesSansCible.length > 0 ? `Aucune cible pour : ${classesSansCible.join(', ')}` :
                    ''
                  }
                  className="inline-flex items-center gap-1 px-4 py-1.5 rounded bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold disabled:opacity-50"
                >
                  👁 Prévisualiser ({nbCopiesSel} copies) →
                </button>
              </div>
            </div>
          </>
        )}

        {/* ═══════════════ ÉTAPE 2 : PREVIEW ═══════════════ */}
        {etape === 'preview' && preview && (
          <>
            <div className="flex-1 overflow-auto p-5 space-y-4 text-sm">
              {/* Compteurs globaux */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="rounded-lg bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 p-3 text-center">
                  <div className="text-2xl font-bold text-blue-700 dark:text-blue-300">{preview.nb_copies}</div>
                  <div className="text-[10px] uppercase text-blue-800 dark:text-blue-400">Copies</div>
                </div>
                <div className="rounded-lg bg-indigo-50 dark:bg-indigo-950/30 border border-indigo-200 dark:border-indigo-800 p-3 text-center">
                  <div className="text-2xl font-bold text-indigo-700 dark:text-indigo-300">{preview.nb_etudiants_distincts}</div>
                  <div className="text-[10px] uppercase text-indigo-800 dark:text-indigo-400">Étudiants</div>
                </div>
                <div className="rounded-lg bg-purple-50 dark:bg-purple-950/30 border border-purple-200 dark:border-purple-800 p-3 text-center">
                  <div className="text-2xl font-bold text-purple-700 dark:text-purple-300">{preview.nb_classes_distinctes}</div>
                  <div className="text-[10px] uppercase text-purple-800 dark:text-purple-400">Classes</div>
                </div>
                <div className="rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 p-3 text-center">
                  <div className="text-2xl font-bold text-amber-700 dark:text-amber-300">{preview.nb_formateurs_distincts}</div>
                  <div className="text-[10px] uppercase text-amber-800 dark:text-amber-400">Formateurs</div>
                </div>
              </div>

              {/* Répartitions */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-2">
                  <div className="font-semibold text-[11px] uppercase text-gray-500 mb-1">Par matière source</div>
                  {Object.entries(preview.par_matiere || {}).map(([k, n]) => (
                    <div key={k} className="text-[11px] flex justify-between border-b border-gray-100 dark:border-gray-800 py-0.5">
                      <span>{k}</span><span className="font-mono font-semibold">{n as number}</span>
                    </div>
                  ))}
                </div>
                <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-2">
                  <div className="font-semibold text-[11px] uppercase text-gray-500 mb-1">Par classe</div>
                  {Object.entries(preview.par_classe || {}).map(([k, n]) => (
                    <div key={k} className="text-[11px] flex justify-between border-b border-gray-100 dark:border-gray-800 py-0.5">
                      <span className="font-mono">{k}</span><span className="font-mono font-semibold">{n as number}</span>
                    </div>
                  ))}
                </div>
                <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-2">
                  <div className="font-semibold text-[11px] uppercase text-gray-500 mb-1">Par formateur</div>
                  {Object.entries(preview.par_formateur || {}).map(([k, n]) => (
                    <div key={k} className="text-[11px] flex justify-between border-b border-gray-100 dark:border-gray-800 py-0.5">
                      <span>{k}</span><span className="font-mono font-semibold">{n as number}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Liste des étudiants concernés */}
              <div>
                <div className="font-semibold text-xs mb-1">Étudiants concernés (100 premiers)</div>
                <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                  <div className="max-h-[38vh] overflow-y-auto">
                    <table className="w-full text-[10px]">
                      <thead className="bg-gray-100 dark:bg-gray-800 sticky top-0">
                        <tr className="text-gray-500">
                          <th className="px-2 py-1 text-left">Code</th>
                          <th className="px-2 py-1 text-left">Matricule</th>
                          <th className="px-2 py-1 text-left">Nom</th>
                          <th className="px-2 py-1 text-left">Classe</th>
                          <th className="px-2 py-1 text-left">Matière actuelle</th>
                          <th className="px-2 py-1 text-left">Formateur</th>
                          <th className="px-2 py-1 text-left">Type</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(preview.echantillon || []).map((e: any, i: number) => (
                          <tr key={i} className="border-b border-gray-100 dark:border-gray-800">
                            <td className="px-2 py-1 font-mono">{e.code_copie}</td>
                            <td className="px-2 py-1">{e.etudiant__matricule}</td>
                            <td className="px-2 py-1">{e.etudiant__nom} {e.etudiant__prenoms}</td>
                            <td className="px-2 py-1 font-mono text-indigo-700 dark:text-indigo-300">{e.etudiant__classe}</td>
                            <td className="px-2 py-1">{e.matiere__nom_matiere}</td>
                            <td className="px-2 py-1">{e.enseignant__nom} {e.enseignant__prenoms}</td>
                            <td className="px-2 py-1">{e.type_examen}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              {/* Récap cibles (une ou plusieurs) */}
              <div className="rounded-lg bg-emerald-50 dark:bg-emerald-950/30 border-2 border-emerald-300 dark:border-emerald-700 p-3 text-sm">
                <div className="text-[11px] uppercase text-emerald-700 dark:text-emerald-400 mb-1">
                  Matière(s) cible(s) — routage par classe
                </div>
                {preview?.dests_by_classe ? (
                  <table className="w-full text-[11px]">
                    <tbody>
                      {Object.entries(preview.dests_by_classe).map(([cls, info]: [string, any]) => (
                        <tr key={cls} className="border-b border-emerald-200/40 dark:border-emerald-800/40">
                          <td className="py-1 pr-2 font-mono font-semibold text-indigo-700 dark:text-indigo-300">{cls}</td>
                          <td className="py-1 px-2 text-emerald-600">→</td>
                          <td className="py-1 px-2 text-emerald-900 dark:text-emerald-100">
                            <strong>{info.nom}</strong>
                            <span className="ml-1 text-[10px] font-mono opacity-70">({info.niveau} · S{info.semestre})</span>
                          </td>
                          <td className="py-1 pl-2 text-right font-mono">{info.nb_copies}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : preview?.dest ? (
                  <div className="font-semibold text-emerald-900 dark:text-emerald-100">
                    {preview.dest.nom} <span className="text-xs font-mono opacity-70">({preview.dest.niveau} S{preview.dest.semestre})</span>
                  </div>
                ) : (
                  <div className="text-[11px] text-gray-500 italic">Aucune cible résolue.</div>
                )}
              </div>

              {/* Alerte niveau différent */}
              {niveauxDifferents && (
                <div className="rounded-lg border border-amber-400 bg-amber-50 dark:bg-amber-950/30 p-3 flex items-start gap-2">
                  <AlertTriangle size={16} className="text-amber-600 mt-0.5 shrink-0" />
                  <div className="text-xs text-amber-900 dark:text-amber-200">
                    ⚠ Certaines cibles ont un niveau qui ne correspond à aucun des niveaux sources
                    ({Array.from(niveauxSources).join(', ')}).
                    <label className="flex items-center gap-2 mt-1">
                      <input type="checkbox" checked={forceNiveau} onChange={(e) => setForceNiveau(e.target.checked)} />
                      <span>Je confirme malgré les niveaux différents</span>
                    </label>
                  </div>
                </div>
              )}

              {message && (
                <div className={`rounded-lg p-2 text-xs ${
                  message.type === 'success'
                    ? 'bg-emerald-50 text-emerald-800 border border-emerald-300 dark:bg-emerald-900/30 dark:text-emerald-200'
                    : 'bg-red-50 text-red-800 border border-red-300 dark:bg-red-900/30 dark:text-red-200'
                }`}>
                  {message.text}
                </div>
              )}
            </div>

            <div className="px-5 py-3 border-t border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50 flex items-center justify-between gap-2">
              <button
                onClick={() => setEtape('selection')}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded border border-gray-300 dark:border-gray-600 text-xs text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                <ArrowLeft size={12} /> Retour sélection
              </button>
              <button
                onClick={lancerTransfert}
                disabled={isLoadingMut || (niveauxDifferents && !forceNiveau)}
                className="inline-flex items-center gap-1 px-4 py-1.5 rounded bg-rose-600 hover:bg-rose-700 text-white text-xs font-semibold disabled:opacity-50"
              >
                <ArrowRightLeft size={14} />
                {isLoadingMut ? 'Transfert en cours…' : `✓ Confirmer transfert (${preview.nb_copies} copies)`}
              </button>
            </div>
          </>
        )}

        {/* ═══════════════ ÉTAPE 3 : TERMINÉ ═══════════════ */}
        {etape === 'termine' && (
          <>
            <div className="flex-1 flex items-center justify-center p-10">
              <div className="text-center max-w-md">
                <CheckCircle2 size={48} className="mx-auto text-emerald-500 mb-3" />
                <div className="text-lg font-bold text-emerald-700 dark:text-emerald-300 mb-2">
                  Transfert effectué avec succès
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-300 mb-1">{message?.text}</div>
                <div className="text-[11px] text-gray-500 mt-3">
                  Traçabilité enregistrée dans l'historique des associations.
                </div>
              </div>
            </div>
            <div className="px-5 py-3 border-t border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50 flex items-center justify-end gap-2">
              <button
                onClick={() => setEtape('selection')}
                className="px-3 py-1.5 rounded border border-gray-300 dark:border-gray-600 text-xs"
              >
                Nouveau transfert
              </button>
              <button
                onClick={onClose}
                className="px-4 py-1.5 rounded bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold"
              >
                Fermer
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default TransfererMatiereModal;
